import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth, API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, CreditCard, FileText, ChevronRight, Tag, X, Check, AlertTriangle, Shield, ArrowLeft } from 'lucide-react';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [invoiceEnabled, setInvoiceEnabled] = useState(false);
  
  // PO Number and Contact Person
  const [poNumber, setPoNumber] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  
  // Company details
  const [orderingAs, setOrderingAs] = useState('individual'); // 'individual' or 'company'
  const [companyName, setCompanyName] = useState('');
  const [companyAbn, setCompanyAbn] = useState('');
  const [userCompanyData, setUserCompanyData] = useState(null); // Store company data from Company Section
  const [userProfile, setUserProfile] = useState(null); // Store full user profile
  
  // Disclaimer state
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [hasAcceptedBefore, setHasAcceptedBefore] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(false);
  
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });


  useEffect(() => {
    // Load quote from session storage
    const storedQuote = sessionStorage.getItem('quote');
    if (storedQuote) {
      const parsedQuote = JSON.parse(storedQuote);
      
      // If we only have quote_id (e.g., from reorder), fetch the full quote
      if (parsedQuote.quote_id && !parsedQuote.items) {
        fetchQuoteById(parsedQuote.quote_id);
      } else {
        setQuote(parsedQuote);
      }
    } else {
      navigate('/get-quote');
    }

    // Pre-fill user info if authenticated
    if (user) {
      setCustomerInfo({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || ''
      });
      
      // Fetch full user profile to get company details
      fetchUserProfile();
      
      // Also fetch company data from Company Section
      fetchUserCompany();
    }
    
    // Check if user has invoice permission
    const checkPaymentOptions = async () => {
      try {
        const headers = {};
        if (isAuthenticated) {
          headers.Authorization = `Bearer ${localStorage.getItem('auth_token')}`;
        }
        const response = await axios.get(`${API}/user/payment-options`, { headers });
        setInvoiceEnabled(response.data.invoice || false);
      } catch (error) {
        console.error('Failed to check payment options:', error);
        setInvoiceEnabled(false);
      }
    };
    checkPaymentOptions();
    
    // Fetch disclaimer text
    const fetchDisclaimer = async () => {
      try {
        const response = await axios.get(`${API}/settings/global`);
        setDisclaimerText(response.data.disclaimer || '');
      } catch (error) {
        console.error('Failed to fetch disclaimer:', error);
      }
    };
    fetchDisclaimer();
    
    // Check if authenticated user has already accepted disclaimer
    const checkDisclaimerStatus = async () => {
      if (isAuthenticated) {
        try {
          const response = await axios.get(`${API}/user/disclaimer-status`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
          });
          setHasAcceptedBefore(response.data.accepted || false);
        } catch (error) {
          console.error('Failed to check disclaimer status:', error);
        }
      }
    };
    checkDisclaimerStatus();
  }, [user, navigate, isAuthenticated]);

  const fetchQuoteById = async (quoteId) => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const fetchedQuote = response.data;
      setQuote(fetchedQuote);
      // Update session storage with full quote data
      sessionStorage.setItem('quote', JSON.stringify(fetchedQuote));
    } catch (error) {
      console.error('Failed to fetch quote:', error);
      toast.error('Failed to load quote. Please try again.');
      navigate('/dashboard');
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setUserProfile(response.data);
      // Pre-fill customer info from profile
      setCustomerInfo(prev => ({
        name: response.data.name || prev.name,
        email: response.data.email || prev.email,
        phone: response.data.phone || prev.phone,
        address: response.data.shipping_address || prev.address
      }));
      // Pre-fill company details from profile (user's saved business info)
      if (response.data.company_name) {
        setCompanyName(response.data.company_name);
      }
      if (response.data.abn) {
        setCompanyAbn(response.data.abn);
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const fetchUserCompany = async () => {
    try {
      const response = await axios.get(`${API}/user/company`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.data.company) {
        setUserCompanyData(response.data.company);
        // Only use company section data if user profile doesn't have company details
        // This ensures user's saved business info takes priority
      }
    } catch (error) {
      // User not in a company - that's fine
      console.log('No company membership found');
    }
  };

  // Handle ordering type change - auto-populate company fields
  const handleOrderingAsChange = (value) => {
    setOrderingAs(value);
    if (value === 'company') {
      // Priority: User Profile > Company Section data
      // User's saved business info takes precedence
      if (userProfile?.company_name) {
        setCompanyName(userProfile.company_name);
      } else if (userCompanyData?.name) {
        setCompanyName(userCompanyData.name);
      }
      
      if (userProfile?.abn) {
        setCompanyAbn(userProfile.abn);
      } else if (userCompanyData?.abn) {
        setCompanyAbn(userCompanyData.abn);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!customerInfo.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!customerInfo.email.trim() || !customerInfo.email.includes('@')) {
      toast.error('Please enter a valid email');
      return false;
    }
    return true;
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setApplyingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        order_total: quote.total,
        customer_email: customerInfo.email
      });
      
      setAppliedCoupon(response.data);
      setDiscount(response.data.discount_amount);
      toast.success(`Coupon applied! You save $${response.data.discount_amount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
      setDiscount(0);
    }
    setApplyingCoupon(false);
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
    setCouponCode('');
    toast.success('Coupon removed');
  };

  const getFinalTotal = () => {
    return Math.max(0, (quote?.total || 0) - discount);
  };

  const handleCheckout = async () => {
    if (!validateForm()) return;
    
    // If user hasn't accepted disclaimer before, show modal
    if (!hasAcceptedBefore && disclaimerText) {
      setDisclaimerOpen(true);
      setPendingCheckout(true);
      return;
    }
    
    await processCheckout();
  };

  const handleAcceptDisclaimer = async () => {
    if (!disclaimerAccepted) {
      toast.error('Please accept the terms to continue');
      return;
    }
    
    // Save acceptance to user profile if authenticated
    if (isAuthenticated) {
      try {
        await axios.post(`${API}/user/accept-disclaimer`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        });
        setHasAcceptedBefore(true);
      } catch (error) {
        console.error('Failed to save disclaimer acceptance:', error);
      }
    }
    
    setDisclaimerOpen(false);
    
    if (pendingCheckout) {
      setPendingCheckout(false);
      await processCheckout();
    }
  };

  const processCheckout = async () => {
    setLoading(true);
    try {
      // Create order with coupon info, PO number, contact person, and company details
      const orderData = {
        quote_id: quote.quote_id,
        customer: customerInfo,
        payment_method: paymentMethod,
        po_number: poNumber.trim() || null,
        contact_person: contactPerson.trim() || null,
        ordering_as: orderingAs,
        company_name: orderingAs === 'company' ? companyName.trim() : null,
        company_abn: orderingAs === 'company' ? companyAbn.trim() : null
      };
      
      // Add coupon info if applied
      if (appliedCoupon) {
        orderData.coupon_id = appliedCoupon.coupon_id;
        orderData.discount_amount = discount;
      }
      
      const orderResponse = await axios.post(`${API}/orders/create`, orderData, {
        headers: user ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {}
      });

      if (paymentMethod === 'stripe') {
        // Create Stripe checkout session
        const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
          order_id: orderResponse.data.order_id,
          origin_url: window.location.origin
        });

        // Redirect to Stripe
        window.location.href = paymentResponse.data.checkout_url;
      } else if (paymentMethod === 'invoice') {
        // Invoice payment - available to all users
        toast.success('Order placed! Invoice will be sent to your email.');
        sessionStorage.removeItem('quote');
        sessionStorage.removeItem('quoteFiles');
        navigate(`/order-success?order_id=${orderResponse.data.order_id}&payment=invoice`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
    }
    setLoading(false);
  };

  if (!quote) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="checkout-page">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Back Button */}
          <button
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/get-quote')}
            className="flex items-center gap-2 text-white/60 hover:text-[#CCFF00] transition-colors mb-6 group"
            data-testid="checkout-back-button"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>{isAuthenticated ? 'Back to Dashboard' : 'Back to Quote'}</span>
          </button>

          <div className="mb-12">
            <span className="overline text-[#CCFF00]">Checkout</span>
            <h1 className="text-3xl sm:text-4xl font-['Chivo'] font-black text-white mt-2">
              Complete Your Order
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Customer Info Form */}
            <div className="lg:col-span-7 space-y-6">
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-lg font-bold text-white mb-6">Contact Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/70">Full Name *</Label>
                    <Input
                      name="name"
                      value={customerInfo.name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      className="bg-white/5 border-white/10 text-white mt-1"
                      data-testid="customer-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Email Address *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={handleInputChange}
                      placeholder="john@company.com"
                      className="bg-white/5 border-white/10 text-white mt-1"
                      data-testid="customer-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Phone Number</Label>
                    <Input
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleInputChange}
                      placeholder="+61 400 000 000"
                      className="bg-white/5 border-white/10 text-white mt-1"
                      data-testid="customer-phone-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Delivery Address</Label>
                    <Input
                      name="address"
                      value={customerInfo.address}
                      onChange={handleInputChange}
                      placeholder="123 Main St, Melbourne VIC 3000"
                      className="bg-white/5 border-white/10 text-white mt-1"
                      data-testid="customer-address-input"
                    />
                  </div>
                </div>

                {/* Individual or Company Selection */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <Label className="text-white/70 mb-3 block">Ordering as</Label>
                  <div className="flex gap-4">
                    <label 
                      className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors flex-1 ${
                        orderingAs === 'individual' ? 'border-[#CCFF00] bg-[#CCFF00]/5' : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="radio"
                        name="orderingAs"
                        value="individual"
                        checked={orderingAs === 'individual'}
                        onChange={(e) => handleOrderingAsChange(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        orderingAs === 'individual' ? 'border-[#CCFF00]' : 'border-white/30'
                      }`}>
                        {orderingAs === 'individual' && <div className="w-2 h-2 bg-[#CCFF00] rounded-full" />}
                      </div>
                      <span className="text-white">Individual</span>
                    </label>
                    
                    <label 
                      className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors flex-1 ${
                        orderingAs === 'company' ? 'border-[#CCFF00] bg-[#CCFF00]/5' : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="radio"
                        name="orderingAs"
                        value="company"
                        checked={orderingAs === 'company'}
                        onChange={(e) => handleOrderingAsChange(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        orderingAs === 'company' ? 'border-[#CCFF00]' : 'border-white/30'
                      }`}>
                        {orderingAs === 'company' && <div className="w-2 h-2 bg-[#CCFF00] rounded-full" />}
                      </div>
                      <span className="text-white">Company</span>
                    </label>
                  </div>
                  
                  {/* Company Details - shown when Company is selected */}
                  {orderingAs === 'company' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label className="text-white/70">Company Name *</Label>
                        <Input
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="Your Company Pty Ltd"
                          className="bg-white/5 border-white/10 text-white mt-1"
                          data-testid="company-name-input"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-white/70">ABN</Label>
                        <Input
                          value={companyAbn}
                          onChange={(e) => setCompanyAbn(e.target.value)}
                          placeholder="12 345 678 901"
                          className="bg-white/5 border-white/10 text-white mt-1"
                          data-testid="company-abn-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Reference (Optional) */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-lg font-bold text-white mb-4">Order Reference <span className="text-white/40 font-normal text-sm">(Optional)</span></h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">PO Number</Label>
                    <Input
                      value={poNumber}
                      onChange={(e) => setPoNumber(e.target.value)}
                      placeholder="e.g., PO-12345"
                      className="bg-white/5 border-white/10 text-white mt-1"
                      data-testid="po-number-input"
                    />
                    <p className="text-white/40 text-xs mt-1">Your purchase order reference</p>
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Contact Person</Label>
                    <Input
                      value={contactPerson}
                      onChange={(e) => setContactPerson(e.target.value)}
                      placeholder="e.g., John Smith"
                      className="bg-white/5 border-white/10 text-white mt-1"
                      data-testid="contact-person-input"
                    />
                    <p className="text-white/40 text-xs mt-1">Person placing this order (if different from account holder)</p>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-lg font-bold text-white mb-6">Payment Method</h2>
                
                <div className="space-y-3">
                  <label 
                    className={`flex items-center gap-4 p-4 border rounded-sm cursor-pointer transition-colors ${
                      paymentMethod === 'stripe' ? 'border-[#CCFF00] bg-[#CCFF00]/5' : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'stripe' ? 'border-[#CCFF00]' : 'border-white/30'
                    }`}>
                      {paymentMethod === 'stripe' && <div className="w-2.5 h-2.5 bg-[#CCFF00] rounded-full" />}
                    </div>
                    <CreditCard className="w-5 h-5 text-white/70" />
                    <div>
                      <p className="text-white font-medium">Pay Now with Card</p>
                      <p className="text-white/50 text-sm">Secure payment via Stripe</p>
                    </div>
                  </label>

                  {invoiceEnabled && (
                    <label 
                      className={`flex items-center gap-4 p-4 border rounded-sm cursor-pointer transition-colors ${
                        paymentMethod === 'invoice' ? 'border-[#CCFF00] bg-[#CCFF00]/5' : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value="invoice"
                        checked={paymentMethod === 'invoice'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'invoice' ? 'border-[#CCFF00]' : 'border-white/30'
                      }`}>
                        {paymentMethod === 'invoice' && <div className="w-2.5 h-2.5 bg-[#CCFF00] rounded-full" />}
                      </div>
                      <FileText className="w-5 h-5 text-white/70" />
                      <div>
                        <p className="text-white font-medium">Pay on Invoice</p>
                        <p className="text-white/50 text-sm">We'll send an invoice after order review</p>
                      </div>
                    </label>
                  )}
                  
                  {!invoiceEnabled && isAuthenticated && (
                    <p className="text-white/40 text-xs mt-2">
                      Need invoice payment options? Contact us to apply for a credit account.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-5">
              <div className="sticky top-24 bg-[#111] border border-white/10 rounded-sm p-6">
                <h2 className="text-lg font-bold text-white mb-6">Order Summary</h2>

                <div className="space-y-3 mb-6">
                  {quote.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-white/70 truncate max-w-[60%]">
                        {item.file_name} x{item.quantity}
                      </span>
                      <span className="text-white font-mono">
                        ${item.total_price.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Coupon Code Section */}
                <div className="border-t border-white/10 pt-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-[#CCFF00]" />
                    <span className="text-white/70 text-sm">Have a coupon code?</span>
                  </div>
                  
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between bg-[#CCFF00]/10 border border-[#CCFF00]/30 p-3 rounded-sm">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#CCFF00]" />
                        <span className="text-[#CCFF00] font-mono font-bold">{appliedCoupon.code}</span>
                        <span className="text-white/50 text-sm">-${discount.toFixed(2)}</span>
                      </div>
                      <button 
                        onClick={removeCoupon}
                        className="text-white/50 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="ENTER CODE"
                        className="bg-white/5 border-white/10 text-white font-mono uppercase flex-1"
                        data-testid="coupon-input"
                      />
                      <Button
                        onClick={applyCoupon}
                        disabled={applyingCoupon || !couponCode.trim()}
                        variant="outline"
                        className="border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
                      >
                        {applyingCoupon ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Apply'
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="border-t border-white/10 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Subtotal</span>
                    <span className="text-white font-mono">${quote.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Delivery</span>
                    <span className="text-white font-mono">${quote.delivery_cost.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#CCFF00]">Discount</span>
                      <span className="text-[#CCFF00] font-mono">-${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg pt-2 border-t border-white/10">
                    <span className="text-white font-bold">Total</span>
                    <span className="text-[#CCFF00] font-bold font-mono">
                      ${getFinalTotal().toFixed(2)} AUD
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none py-6 mt-6 neon-glow"
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {paymentMethod === 'stripe' ? 'Pay Now' : 'Place Order'}
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-white/30 text-xs text-center mt-4">
                  By placing your order, you agree to our Terms of Service
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Disclaimer Modal */}
      <Dialog open={disclaimerOpen} onOpenChange={setDisclaimerOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Shield className="w-5 h-5 text-[#CCFF00]" />
              Terms & Conditions
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Please read and accept our terms before placing your order
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[50vh] pr-2 my-4">
            <div className="space-y-4 text-white/80 text-sm leading-relaxed">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-xl font-bold text-white mb-3" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-lg font-bold text-white mb-2 mt-4" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-base font-bold text-white mb-2 mt-3" {...props} />,
                  p: ({node, ...props}) => <p className="mb-3 leading-relaxed whitespace-pre-line" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-3 space-y-1" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...props} />,
                  li: ({node, ...props}) => <li className="mb-1 whitespace-pre-line" {...props} />,
                  strong: ({node, ...props}) => <strong className="text-white font-bold" {...props} />,
                  em: ({node, ...props}) => <em className="italic" {...props} />,
                  a: ({node, ...props}) => <a className="text-[#CCFF00] hover:underline" {...props} />,
                }}
              >
                {disclaimerText}
              </ReactMarkdown>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-4 space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="accept-disclaimer"
                checked={disclaimerAccepted}
                onCheckedChange={setDisclaimerAccepted}
                className="mt-1 border-white/30 data-[state=checked]:bg-[#CCFF00] data-[state=checked]:border-[#CCFF00]"
              />
              <label 
                htmlFor="accept-disclaimer" 
                className="text-sm text-white/70 cursor-pointer"
              >
                I have read and agree to the terms and conditions above. I understand that 3D Print Bureau 
                is not liable for issues arising from file errors, design defects, or improper material selection.
              </label>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setDisclaimerOpen(false);
                  setPendingCheckout(false);
                }}
                className="flex-1 border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAcceptDisclaimer}
                disabled={!disclaimerAccepted}
                className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
              >
                <Check className="w-4 h-4 mr-2" />
                Accept & Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
